import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import Loader from "@/components/loader";
import {
  Empty,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import type { SystemRole } from "@/lib/role-hooks";
import { useRequireRole } from "@/lib/role-hooks";

interface RoleGuardProps {
  requiredRole: SystemRole;
  children: ReactNode;
  fallback?: ReactNode;
  loadingFallback?: ReactNode;
}

export function RoleGuard({
  requiredRole,
  children,
  fallback,
  loadingFallback,
}: RoleGuardProps) {
  const { hasAccess, isLoading } = useRequireRole(requiredRole);

  if (isLoading) {
    return loadingFallback || <Loader label="Loading..." />;
  }

  if (!hasAccess) {
    return (
      fallback || (
        <div className="p-4">
          <Empty>
            <EmptyMedia variant="icon">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </EmptyMedia>
            <EmptyTitle>Access Denied</EmptyTitle>
            <EmptyDescription>
              You don't have permission to access this feature.
            </EmptyDescription>
          </Empty>
        </div>
      )
    );
  }

  return <>{children}</>;
}

export function SiteManagerGuard({
  children,
  fallback,
  loadingFallback,
}: Omit<RoleGuardProps, "requiredRole">) {
  return (
    <RoleGuard
      requiredRole="site_manager"
      fallback={fallback}
      loadingFallback={loadingFallback}
    >
      {children}
    </RoleGuard>
  );
}

export function AdminGuard({
  children,
  fallback,
  loadingFallback,
}: Omit<RoleGuardProps, "requiredRole">) {
  return (
    <RoleGuard
      requiredRole="admin"
      fallback={fallback}
      loadingFallback={loadingFallback}
    >
      {children}
    </RoleGuard>
  );
}
